/**
 * useCanAccess Hook
 *
 * Programmatic permission checking based on user's role.
 * Returns boolean indicating if current user has required permissions.
 *
 * @module hooks/useCanAccess
 *
 * @example
 * function Dashboard() {
 *   const canConnect = useCanAccess(['admin']);
 *   const canSync = useCanAccess(['admin', 'analyst']);
 *
 *   return (
 *     <div>
 *       {canConnect && <ConnectButton />}
 *       {canSync && <SyncButton />}
 *     </div>
 *   );
 * }
 */

import { useMemo } from "react";
import { hasPermission } from "@/utils/rolePermissions";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import type { WorkspaceRole } from "@/types";

/**
 * Hook to check if current user has required role permissions
 * Uses hierarchical permission model (admin > analyst > viewer)
 *
 * @param requiredRoles - Array of roles that can access
 * @returns True if current user has sufficient permission
 *
 * @example
 * const isAdmin = useCanAccess(['admin']);
 * const canViewData = useCanAccess(['admin', 'analyst']);
 */
export const useCanAccess = (requiredRoles: WorkspaceRole[]): boolean => {
  const { workspace } = useWorkspace();

  const hasUserPermission = useMemo(() => {
    // No workspace context - return false
    if (!workspace?.userRole) {
      return false;
    }
    // Check permission using hierarchical comparison
    return hasPermission(workspace.userRole, requiredRoles);
  }, [workspace?.userRole, requiredRoles]);

  return hasUserPermission;
};

export default useCanAccess;
